import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
} from '@/lib/firebase/firestore'
import type { ServiceDocument, PriceUnit } from '@/types'
import type { ServiceFormValues } from '../types'

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const servicesCol = () => typedCollection<ServiceDocument>('services')

type ServiceCreatePayload = Omit<ServiceDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

function derivePriceUnit(pricingType: string): PriceUnit | null {
  switch (pricingType) {
    case 'flat':        return 'flat'
    case 'hourly':      return 'perHour'
    case 'perMinute':   return 'perMinute'
    case 'perHeadshot': return 'perHeadshot'
    default:            return null
  }
}

function buildCatalogFields(data: ServiceFormValues): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  fields.serviceKey = data.serviceKey.trim() || null
  fields.priceUnit = derivePriceUnit(data.pricingType)
  fields.basePrice = data.price !== '' ? data.price : null
  fields.isEvent = data.isEvent
  fields.hasComplexity = data.hasComplexity
  fields.showAudioOption = data.showAudioOption
  fields.setupTime = data.setupTime !== '' ? data.setupTime : null
  fields.addonIds = data.addonIds ?? []
  fields.providerPositionIds = data.providerPositionIds ?? []
  if (data.hasComplexity) {
    fields.complexityBase = {
      ...(data.complexitySimplePrice !== '' && { simple: data.complexitySimplePrice }),
      ...(data.complexityModeratePrice !== '' && { moderate: data.complexityModeratePrice }),
      ...(data.complexityComplexPrice !== '' && { complex: data.complexityComplexPrice }),
    }
    fields.complexityTexts = {
      ...(data.complexitySimpleText.trim() && { simple: data.complexitySimpleText.trim() }),
      ...(data.complexityModerateText.trim() && { moderate: data.complexityModerateText.trim() }),
      ...(data.complexityComplexText.trim() && { complex: data.complexityComplexText.trim() }),
    }
  } else {
    fields.complexityBase = null
    fields.complexityTexts = null
  }
  return fields
}

export const adminServicesService = {
  async createService(data: ServiceFormValues): Promise<string> {
    const slug = toSlug(data.title)
    const payload: ServiceCreatePayload = {
      title: data.title.trim(),
      slug,
      description: data.description.trim(),
      category: data.category.trim(),
      pricingType: data.pricingType,
      ...(data.price !== '' && { price: data.price }),
      ...(data.priceMax !== '' && { priceMax: data.priceMax }),
      ...(data.pricingNote.trim() && { pricingNote: data.pricingNote.trim() }),
      ...(data.ctaLabel.trim() && { ctaLabel: data.ctaLabel.trim() }),
      ...(data.ctaUrl.trim() && { ctaUrl: data.ctaUrl.trim() }),
      isPublished: data.isPublished,
      order: data.order,
      ...(data.photoMediaId.trim() && {
        photoMediaId: data.photoMediaId.trim(),
        photoUrl: data.photoUrl.trim(),
        photoFocusX: data.photoFocusX,
        photoFocusY: data.photoFocusY,
      }),
      ...buildCatalogFields(data),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(servicesCol(), payload as unknown as ServiceDocument)
    return ref.id
  },

  async updateService(id: string, data: Partial<ServiceFormValues>): Promise<void> {
    const ref = typedDoc<ServiceDocument>('services', id)
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }

    if (data.title !== undefined) {
      updates.title = data.title.trim()
      updates.slug = toSlug(data.title)
    }
    if (data.description !== undefined) updates.description = data.description.trim()
    if (data.category !== undefined) updates.category = data.category.trim()
    if (data.pricingType !== undefined) updates.pricingType = data.pricingType
    if (data.price !== undefined) updates.price = data.price !== '' ? data.price : null
    if (data.priceMax !== undefined) updates.priceMax = data.priceMax !== '' ? data.priceMax : null
    if (data.pricingNote !== undefined) updates.pricingNote = data.pricingNote.trim()
    if (data.ctaLabel !== undefined) updates.ctaLabel = data.ctaLabel.trim()
    if (data.ctaUrl !== undefined) updates.ctaUrl = data.ctaUrl.trim()
    if (data.isPublished !== undefined) updates.isPublished = data.isPublished
    if (data.order !== undefined) updates.order = data.order
    if (data.photoMediaId !== undefined) {
      updates.photoMediaId = data.photoMediaId.trim() || null
      updates.photoUrl = data.photoUrl?.trim() || null
      updates.photoFocusX = data.photoMediaId.trim() ? (data.photoFocusX ?? 0.5) : null
      updates.photoFocusY = data.photoMediaId.trim() ? (data.photoFocusY ?? 0.5) : null
    }
    // Catalog fields — always write all together when any is present
    if (data.serviceKey !== undefined) Object.assign(updates, buildCatalogFields(data as ServiceFormValues))
    // addonIds standalone update (when serviceKey is not being updated)
    if (data.addonIds !== undefined && data.serviceKey === undefined) {
      updates.addonIds = data.addonIds
    }
    if (data.providerPositionIds !== undefined && data.serviceKey === undefined) {
      updates.providerPositionIds = data.providerPositionIds
    }

    await updateDocument(ref, updates as unknown as ServiceDocument)
  },

  async deleteService(id: string): Promise<void> {
    const ref = typedDoc<ServiceDocument>('services', id)
    await deleteDocument(ref)
  },

  async getAllServices(): Promise<Array<ServiceDocument & { id: string }>> {
    return getCollection(servicesCol(), orderBy('order', 'asc'))
  },

  async getServiceById(id: string): Promise<(ServiceDocument & { id: string }) | null> {
    const ref = typedDoc<ServiceDocument>('services', id)
    const data = await getDocument(ref)
    return data ? { ...data, id } : null
  },
}
